/**
 * Spinner — React (web) implementation.
 *
 * An inline-SVG circular arc with a gradient stroke (opaque → transparent),
 * rotated continuously by a transform-only CSS keyframe (compositor thread, no
 * per-frame React re-render). All colour/size/duration values come from
 * Spinner.ts — no raw hex or pixel literals.
 */

/// <reference lib="dom" />

import React from 'react';
import {
  SPINNER_DEFAULT_SIZE,
  SPINNER_DURATION_MS,
  spinnerGeometry,
  spinnerGradientBaseFor,
} from './Spinner';
import type { SpinnerTone } from './Spinner';

const SPINNER_STYLE_ID = 'galapagos-spinner-style';
const SPINNER_ANIMATION = 'galapagos-spinner-spin';

function ensureSpinnerStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SPINNER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SPINNER_STYLE_ID;
  style.textContent = `@keyframes ${SPINNER_ANIMATION} { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

export interface WebSpinnerProps {
  tone?: SpinnerTone;
  size?: number;
  accessibilityLabel?: string;
  'data-testid'?: string;
}

export function Spinner({
  tone = 'default',
  size = SPINNER_DEFAULT_SIZE,
  accessibilityLabel,
  'data-testid': dataTestId,
}: WebSpinnerProps) {
  // Inject after commit but before paint — a DOM mutation during render would
  // break the pure-render contract under concurrent rendering.
  React.useLayoutEffect(() => {
    ensureSpinnerStyle();
  }, []);

  // Unique per instance so multiple spinners don't collide on the SVG defs id.
  const gradientId = React.useId();
  const geo = spinnerGeometry(size);
  const base = spinnerGradientBaseFor(tone);

  const style: React.CSSProperties = {
    display: 'inline-flex',
    width: size,
    height: size,
    animation: `${SPINNER_ANIMATION} ${SPINNER_DURATION_MS}ms linear infinite`,
    flexShrink: 0,
  };

  return (
    <span
      role="progressbar"
      aria-busy="true"
      aria-label={accessibilityLabel}
      data-testid={dataTestId}
      style={style}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={base} stopOpacity={1} />
            <stop offset="1" stopColor={base} stopOpacity={0} />
          </linearGradient>
        </defs>
        <circle
          cx={geo.center}
          cy={geo.center}
          r={geo.radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={geo.stroke}
          strokeLinecap="round"
          strokeDasharray={`${geo.arcLength} ${geo.gapLength}`}
        />
      </svg>
    </span>
  );
}
